import { Component, Suspense, lazy, useState, type ReactNode } from "react";
import type {
  Drivability,
  Estimate,
  EstimateLine,
  EstimateTotals,
  PricingBasis,
  RepairScope,
  VehicleValueRange,
} from "../shared/types";
import { breakdownRows } from "../shared/breakdown";
import type { ShopContext } from "./Shops";
import { StatBars } from "./GarageCar";
import { GarageView } from "./GarageView";
import { ClaimOrCash } from "./ClaimOrCash";
import { TotalLoss } from "./TotalLoss";
import { Glyph } from "./mapIcons";

// The shop section pulls in Leaflet — split it out so the core flow stays light.
const ShopsSection = lazy(() => import("./Shops"));

// A shop-section failure (chunk load, render error) must never take down the
// estimate the user just generated — degrade to neutral guidance instead.
class ShopsBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <div className="card">
          <h2>Repair shops near you</h2>
          <p className="sub">
            We couldn't load the shop map right now. Any qualified collision
            repair shop can inspect the damage.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const usd = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function severityClass(s: number): string {
  if (s >= 7) return "sev-high";
  if (s >= 4) return "sev-mid";
  return "sev-low";
}

const money = (m: { low: number; high: number }) =>
  m.high > 0 ? `${usd(m.low)} – ${usd(m.high)}` : usd(0);
const hoursLabel = (h?: { low: number; high: number }) =>
  h ? (h.low === h.high ? `${h.low} hr` : `${h.low}–${h.high} hr`) : "";

const SCOPE_LABEL: Record<RepairScope, string> = {
  repair: "Repair",
  replace: "Replace",
  "repair-or-replace": "Repair or replace",
};

// One part's part/labor/refinish lines. The point of showing these is that a
// driver comparing two shop quotes needs to see WHICH of the three moved —
// a cheaper quote is usually a cheaper part or fewer hours, not a better deal.
function PartLines({ lines }: { lines: EstimateLine[] }) {
  return (
    <ul className="lines">
      {lines.map((l) => (
        <li key={l.id} className={`line line-${l.kind}`}>
          <span className="line-label">
            {l.kind === "part" ? "Part" : l.kind === "refinish" ? "Refinish" : "Labor"}
            {l.hours && <em className="line-hours"> {hoursLabel(l.hours)}</em>}
            {l.rate && l.hours && (
              <em className="line-rate">
                {" "}
                @ ${l.rate.low}–{l.rate.high}/hr
              </em>
            )}
          </span>
          <span className="line-amount">{money(l.amount)}</span>
        </li>
      ))}
    </ul>
  );
}

// The educational core of the whole tool: published labor times assume a new
// part going onto an otherwise undamaged car, and roughly fifty operations are
// excluded from them by rule. Those are the lines that turn a "$3,000 estimate"
// into a $4,200 bill, so they are shown as their own section rather than
// quietly folded into the parts above.
function CostBreakdown({
  totals,
  basis,
  operationLines,
}: {
  totals: EstimateTotals;
  basis?: PricingBasis;
  operationLines: EstimateLine[];
}) {
  const [open, setOpen] = useState(false);
  // Row set is shared with the /r/:id report page so the two can't drift.
  const rows = breakdownRows(totals, basis);
  const notIncluded = operationLines.filter((l) => l.notIncluded);

  return (
    <div className="card">
      <h2>How this range is built</h2>
      <table className="breakdown">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th>{r.label}</th>
              <td className="bd-meta">{r.meta ?? hoursLabel(r.hours)}</td>
              <td className="bd-amount">{money(r.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {notIncluded.length > 0 && (
        <>
          <button className="linkish" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Show"} the {notIncluded.length} operations
          </button>
          {open && (
            <ul className="lines">
              {notIncluded.map((l) => (
                <li key={l.id} className="line">
                  <span className="line-label">
                    {l.label}
                    {l.hours && <em className="line-hours"> {hoursLabel(l.hours)}</em>}
                  </span>
                  <span className="line-amount">{money(l.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// Drive-or-tow banner. Shown ONLY when the engine recommends a tow: a "you're
// probably fine to drive" notice would read as a clearance we can't give from a
// questionnaire, so the drivable case renders nothing at all.
function TowAlert({ drivability }: { drivability: Drivability }) {
  if (drivability.status !== "tow-recommended") return null;
  return (
    <div className="tow-alert" role="alert">
      <p className="tow-head">
        <Glyph name="tow" size={20} /> {drivability.headline}
      </p>
      <p className="tow-summary">{drivability.summary}</p>
      <ul className="tow-reasons">
        {drivability.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>
      <p className="tow-advice">{drivability.advice}</p>
    </div>
  );
}

// Phase 6 — prefilled share actions. No backend delivery: everything is a
// client-side action on the public report URL (maximizes shareability).
function ShareCard({
  reportId,
  estimate,
  selfEmail,
}: {
  reportId: string;
  estimate: Estimate;
  selfEmail?: string;
}) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/r/${reportId}`;
  const range = `${usd(estimate.totalCostRange.low)} – ${usd(estimate.totalCostRange.high)}`;
  const shareText = `Educational car damage pricing guide (${range}): ${url}`;
  const subject = "Car damage pricing guide (educational)";

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  }

  return (
    <div className="card">
      <h2>Share this report</h2>
      <p className="sub">
        Your pricing guide lives at an unlisted link. Copy or send it now, because it's
        the only way back to this report. Anyone with the link can view it (no
        account needed), and it auto-deletes after ~60 days.
      </p>
      <div className="share">
        <button className="share-btn" onClick={copy}>
          {copied ? "Copied ✓" : "Copy link"}
        </button>
        <a
          className="share-btn"
          href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareText)}`}
        >
          Email
        </a>
        <a className="share-btn" href={`sms:?&body=${encodeURIComponent(shareText)}`}>
          Text
        </a>
        {selfEmail && (
          <a
            className="share-btn"
            // RFC 6068: encode only what's required — a percent-encoded '@'
            // breaks the address in some mail handlers.
            href={`mailto:${selfEmail.replace(/[%/?#&;=+\s]/g, (c) => encodeURIComponent(c))}?subject=${encodeURIComponent("Your damage pricing guide")}&body=${encodeURIComponent(shareText)}`}
          >
            Email me my copy
          </a>
        )}
        {"share" in navigator && (
          <button
            className="share-btn"
            onClick={() => navigator.share({ title: subject, url }).catch(() => {})}
          >
            More…
          </button>
        )}
      </div>
      <a className="locator" href={url} target="_blank" rel="noopener noreferrer">
        View the public report page ↗
      </a>
    </div>
  );
}

export function Results({
  estimate,
  reportId,
  shopCtx,
  selfEmail,
  garageCar,
  onRestart,
}: {
  estimate: Estimate;
  reportId?: string;
  shopCtx?: ShopContext;
  selfEmail?: string;
  garageCar?: {
    color: string;
    insurerName?: string;
    zip?: string;
    collisionType: string;
    impactPoints: string[];
    symptoms: string[];
  };
  onRestart: () => void;
}) {
  const { damagedParts, hiddenDamage, totalCostRange } = estimate;
  // Owned here so the two money cards below share one answer: the total-loss
  // card is where "what's it worth?" gets asked, and the claim card reads the
  // result rather than asking the same question again further down the page.
  const [vehicleValue, setVehicleValue] = useState<VehicleValueRange | null>(null);

  return (
    <div className="results">
      {estimate.drivability && <TowAlert drivability={estimate.drivability} />}

      {garageCar && (
        <div className="garage">
          <GarageView car={garageCar} />
          <StatBars
            title="Estimated damage profile"
            preview={{
              partCount: damagedParts.length,
              maxSeverity: damagedParts.reduce((m, p) => Math.max(m, p.severity), 0),
              hiddenProb: hiddenDamage.probability,
            }}
          />
        </div>
      )}

      <div className="card">
        <h2>Estimated repair range</h2>
        <p className="total">
          {usd(totalCostRange.low)} – {usd(totalCostRange.high)}
        </p>
        {hiddenDamage.expectedSupplementRange &&
          hiddenDamage.expectedSupplementRange.high > 0 && (
            <p className="supp">
              {hiddenDamage.expectedSupplementRange.low === 0
                ? `Up to ${usd(hiddenDamage.expectedSupplementRange.high)} is sometimes added`
                : `+ ${usd(hiddenDamage.expectedSupplementRange.low)}–${usd(
                    hiddenDamage.expectedSupplementRange.high,
                  )} is typically added`}{" "}
              after teardown at this hidden-damage level (industry-average
              supplement).
            </p>
          )}
        {estimate.pricing && estimate.pricing.vehicleClass !== "mainstream" && (
          <p className="sub">
            Priced against{" "}
            {estimate.pricing.vehicleClass === "economy"
              ? "economy-class"
              : estimate.pricing.vehicleClass === "ev"
                ? "electric-vehicle"
                : "luxury-class"}{" "}
            part costs and labor times.
          </p>
        )}
        <p className="sub">
          Parts, labor hours at local rates, refinish, materials and the
          operations published labor times leave out. Your actual quote depends
          on a shop's in-person inspection.
        </p>
      </div>

      {estimate.totals && (
        <CostBreakdown
          totals={estimate.totals}
          basis={estimate.basis}
          operationLines={estimate.operationLines ?? []}
        />
      )}

      {/* Both sit directly under the range because the range is their input —
          the number they just read is what makes either question worth asking.
          Total loss goes first: "will it be repaired at all" logically precedes
          "who pays for the repair", and a likely total changes what the claim
          card's answer is even for. */}
      <TotalLoss
        estimate={estimate}
        zip={shopCtx?.zip}
        onVehicleValue={setVehicleValue}
      />
      <ClaimOrCash
        estimate={estimate}
        insurerId={shopCtx?.insurer?.id}
        vehicleValue={vehicleValue}
      />

      <div className="card">
        <h2>Hidden-damage risk</h2>
        <div className={`hd-band hd-${hiddenDamage.band}`}>
          {hiddenDamage.band.toUpperCase()} · {Math.round(hiddenDamage.probability * 100)}%
        </div>
        <p className="sub">{hiddenDamage.explanation}</p>
        {hiddenDamage.drivers.length > 0 && (
          <ul className="drivers">
            {hiddenDamage.drivers.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
      </div>

      {/* The inspection-only fallback produces no parts at all — an empty
          list reads as a bug, so the card sits out entirely and the breakdown
          above carries the single inspection line. */}
      {damagedParts.length > 0 && (
      <div className="card">
        <h2>Likely affected parts</h2>
        <ul className="parts">
          {damagedParts.map((p) => (
            <li key={p.part} className="part">
              <div className="part-head">
                <span className="part-label">{p.label}</span>
                <span className="part-cost">
                  {p.costRange.high > 0
                    ? `${usd(p.costRange.low)} – ${usd(p.costRange.high)}`
                    : "Inspection"}
                </span>
              </div>
              <div className="sev-track">
                <div
                  className={`sev-fill ${severityClass(p.severity)}`}
                  style={{ width: `${p.severity * 10}%` }}
                />
              </div>
              <div className="part-meta">
                <span className="sev-label">Severity {p.severity}/10</span>
                {p.scope && (
                  <span className={`scope-tag scope-${p.scope}`}>
                    {SCOPE_LABEL[p.scope]}
                  </span>
                )}
              </div>
              {p.lines && p.lines.length > 0 && <PartLines lines={p.lines} />}
            </li>
          ))}
        </ul>
        <p className="sub">
          "Repair or replace" means the damage sits in the band where a shop
          could reasonably do either. The low end assumes the panel is saved,
          the high end assumes it is bought.
        </p>
      </div>
      )}

      {shopCtx && (
        <ShopsBoundary>
          <Suspense
            fallback={
              <div className="card">
                <h2>Repair shops near you</h2>
                <p className="sub">Loading shop map…</p>
              </div>
            }
          >
            <ShopsSection
              ctx={shopCtx}
              reportUrl={reportId ? `${window.location.origin}/r/${reportId}` : undefined}
            />
          </Suspense>
        </ShopsBoundary>
      )}

      {reportId && (
        <ShareCard reportId={reportId} estimate={estimate} selfEmail={selfEmail} />
      )}

      <div className="nav">
        <button
          className="ghost"
          onClick={() => {
            if (
              window.confirm(
                "Start over? Copy your report link first, because it's the only way back to this report.",
              )
            )
              onRestart();
          }}
        >
          Start over
        </button>
      </div>
    </div>
  );
}
